<template>
  <v-dialog v-model="open" max-width="1200px" :scrollable="true">
    <v-card>
      <div class="d-flex overflow-hidden">
        <div style="max-width: 400px" class="elevation-5 d-flex flex-column">
          <dialog-title
            v-model="open"
            :title="
              `${workflow == workflows.add ? 'Add' : 'Update'} Tenant User`
            "
            :disabled="loading"
          ></dialog-title>
          <ValidationObserver
            ref="observer"
            tag="form"
            @submit.prevent="onSubmit"
            class="flex-fill d-flex flex-column overflow-hidden"
          >
            <v-card-text class="flex-fill h-100 overflow-auto">
              <v-row>
                <v-col cols="12">
                  Fill out the form below to add a new
                  <strong>Tenant User</strong>.
                </v-col>
                <v-col cols="12">
                  <ValidationProvider
                    rules="required|email|max:255"
                    v-slot="{ errors }"
                    vid="Id"
                  >
                    <v-text-field
                      v-model="item.id"
                      label="Email*"
                      :outlined="workflow == workflows.add"
                      :filled="workflow == workflows.update"
                      :error-messages="errors"
                      :disabled="saving && workflow == workflows.add"
                      :readonly="workflow == workflows.update"
                      autocomplete="email"
                    ></v-text-field>
                  </ValidationProvider>
                  <v-slide-y-transition group>
                    <v-alert
                      v-if="!loading && isAdmin"
                      type="error"
                      border="left"
                      key="adminAlert"
                    >
                      Server admins cannot be added as tenant users.
                    </v-alert>
                    <v-alert
                      v-else-if="
                        !loading && isTenantUser && workflow == workflows.add
                      "
                      type="error"
                      border="left"
                      key="existsAlert"
                    >
                      A user with this email address has already been added to
                      this tenant.
                    </v-alert>
                    <v-alert
                      key="existsWarn"
                      v-else-if="
                        !loading &&
                          workflow == workflows.add &&
                          !isNew &&
                          !isAdmin
                      "
                      border="left"
                      color="primary white--text"
                    >
                      A user with this email address was found under a different
                      tenant. Their permissions will be expanded.
                    </v-alert>
                  </v-slide-y-transition>
                </v-col>
                <v-slide-y-transition>
                  <v-col
                    cols="12"
                    v-if="
                      $refs.observer &&
                        $refs.observer.fields.Id.valid &&
                        dbItemLoaded
                    "
                  >
                    <v-row>
                      <v-col cols="12" sm="6">
                        <ValidationProvider
                          rules="required|min:2|max:255"
                          v-slot="{ errors }"
                          vid="FirstName"
                        >
                          <v-text-field
                            v-model="item.firstName"
                            label="First Name*"
                            outlined
                            :error-messages="errors"
                            :disabled="
                              loading ||
                                isAdmin ||
                                (isTenantUser && workflow === workflows.add)
                            "
                          ></v-text-field>
                        </ValidationProvider>
                      </v-col>
                      <v-col cols="12" sm="6">
                        <ValidationProvider
                          rules="required|min:2|max:255"
                          v-slot="{ errors }"
                          vid="LastName"
                        >
                          <v-text-field
                            v-model="item.lastName"
                            label="Last name*"
                            outlined
                            :error-messages="errors"
                            :disabled="
                              loading ||
                                isAdmin ||
                                (isTenantUser && workflow === workflows.add)
                            "
                          ></v-text-field>
                        </ValidationProvider>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-slide-y-transition>
              </v-row>
              <v-slide-y-transition>
                <div
                  v-if="
                    $refs.observer &&
                      $refs.observer.fields.Id.valid &&
                      dbItemLoaded
                  "
                >
                  <v-divider class="mb-6"></v-divider>
                  <Password
                    ref="password"
                    v-model="item.password"
                    :required="isNew"
                    :disabled="
                      loading ||
                        isAdmin ||
                        (isTenantUser && workflow === workflows.add)
                    "
                  ></Password>
                </div>
              </v-slide-y-transition>
            </v-card-text>
            <div class="px-4 py-3 d-flex align-center flex-grow-0">
              <small class="flex-fill">*indicates required field</small>
              <v-btn
                :disabled="loading"
                text
                color="primary"
                @click="open = false"
              >
                Cancel
              </v-btn>
              <v-btn
                :loading="loading"
                type="submit"
                color="primary"
                :disabled="!canSubmit"
              >
                Save
              </v-btn>
            </div>
          </ValidationObserver>
        </div>
        <v-divider
          vertical
          class="flex-shrink-0"
          style="z-index: 9"
        ></v-divider>
        <div
          class="flex-fill d-flex flex-column overflow-hidden"
          style="position: relative"
        >
          <div class="flex-grow-0">
            <v-toolbar elevation="6" z>
              <div class="d-flex align-center position-relative w-100">
                <div class="text-h6 flex-fill">Entity Access</div>
                <v-text-field
                  solo
                  outlined
                  hide-details
                  rounded
                  dense
                  flat
                  v-model="entitySearch"
                  placeholder="Search"
                  type="search"
                  style="max-width: 200px"
                  class="mr-2"
                >
                  <template v-slot:prepend-inner>
                    <v-icon class="mr-1">$search</v-icon>
                  </template>
                  <template v-slot:append v-if="entitySearch.length > 0">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <a
                          href="#"
                          v-on="on"
                          v-bind="{ attrs }"
                          small
                          @click.prevent="entitySearch = ''"
                        >
                          <v-icon class="ml-1">$close</v-icon>
                        </a>
                      </template>
                      <span>Clear Search</span>
                    </v-tooltip>
                  </template>
                </v-text-field>
                <v-tooltip top>
                  <template v-slot:activator="{ attrs, on }">
                    <v-btn icon v-bind="attrs" v-on="on" @click="loadEntities">
                      <v-icon>$refresh</v-icon>
                    </v-btn>
                  </template>
                  <span>Reload Entities</span>
                </v-tooltip>
              </div>

              <v-progress-linear
                :active="loadingEntities"
                indeterminate
                absolute
                top
              ></v-progress-linear>
            </v-toolbar>
          </div>
          <v-divider class="flex-shrink-0"></v-divider>
          <div v-if="entityBreadCrumbs.length > 0 && entitySearch.length == 0">
            <div class="px-3 py-2 accent white--text">
              <template v-for="(item, i) in entityBreadCrumbs">
                <template v-if="i < entityBreadCrumbs.length - 1">
                  <a
                    :key="item.value"
                    href="#"
                    class="white--text font-weight-bold"
                    @click.prevent="
                      entityPath = item.path;
                      entitySearch = '';
                    "
                  >
                    <v-icon
                      v-if="
                        entityOwnerClaims.some((x) => x.value === item.path)
                      "
                      class="mr-1 white--text"
                      >$checked</v-icon
                    >
                    {{ item.text }}</a
                  >
                  <span :key="i + '_divider'" class="mx-2">></span>
                </template>
                <span :key="item.value" v-else>
                  <v-icon
                    v-if="entityOwnerClaims.some((x) => x.value === item.path)"
                    class="mr-1 white--text"
                    >$check</v-icon
                  >{{ item.text }}</span
                >
              </template>
            </div>
          </div>
          <div class="flex-fill grey pa-4 overflow-auto position-relative">
            <div class="mx-auto" style="max-width: 600px">
              <div v-if="entityPageItems.length > 0" class="text-caption mb-2">
                {{ entityPageDetail }}
              </div>
              <v-card
                v-for="item in entityPageItems"
                :key="item.value"
                rounded="0"
                class="mb-2 w-100"
              >
                <v-card-text class="d-flex align-center">
                  <v-btn
                    :disabled="
                      entityPathOwner ||
                        loadingEntities ||
                        !($refs.observer && $refs.observer.fields.Id.valid)
                    "
                    icon
                    @click="toggleEntityOwnership(item.value)"
                  >
                    <v-icon
                      v-if="
                        item.isSelected ||
                          entityPathOwner ||
                          item.hasChildSelected
                      "
                      :color="
                        entityPathOwner || item.hasChildSelected
                          ? 'grey'
                          : 'primary'
                      "
                      >$checked</v-icon
                    >
                    <v-icon v-else>$unchecked</v-icon>
                  </v-btn>
                  <span
                    class="flex-fill text-truncate"
                    v-html="item.text"
                  ></span>
                  <v-tooltip v-if="item.hasChildren">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        @click="
                          entityPath = item.value;
                          entitySearch = '';
                          entityPage = 1;
                        "
                        fab
                        x-small
                        elevation="0"
                        :color="
                          item.hasChildSelected ? 'primary' : 'grey lighten-2'
                        "
                        v-on="on"
                        v-bind="attrs"
                        :disabled="loadingEntities"
                      >
                        <v-icon>$right</v-icon>
                      </v-btn>
                    </template>
                    <span>Children</span>
                  </v-tooltip>
                </v-card-text>
              </v-card>

              <div
                key="not-found"
                v-if="entityOptions.length === 0"
                class="mt-2 text-center"
              >
                - No Items Found -
              </div>
              <div class="px-4 py-3" v-if="entityPageItems.length > 0">
                <v-pagination
                  v-model="entityPage"
                  :length="entityPageCount"
                ></v-pagination>
              </div>
            </div>
          </div>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import Vue from "vue";
import debounce from "debounce";

import DialogTitle from "../../components/DialogTitle.vue";
import Password from "../../components/password.vue";

import { roles, tenantClaimTypes, settings } from "../../constants/Identity";
import entityService from "../../services/entityService";
import userService from "../../services/userService";
import sortBy from "../../utilities/sortBy";

const newUser = () => ({
  id: "",
  password: "",
  firstName: "",
  lastName: "",
  tenantClaims: [],
});

const workflows = {
  add: "add",
  update: "update",
};

export default {
  name: "TenantUser",
  components: { DialogTitle, Password },
  data: () => ({
    open: false,
    pendingStateChanges: 0,
    item: newUser(),
    dbItemLoaded: false,
    dbItem: null,
    saving: false,
    entities: [],
    entityPathSeparator: settings.entityPathSeparator,
    entityPath: "",
    entitySearch: "",
    entityPage: 1,
    entityPageSize: 10,
    loadingEntities: false,
    workflow: workflows.add,
  }),
  computed: {
    loading() {
      return this.pendingStateChanges > 0;
    },
    tenantId() {
      return this.$store.state.tenantId;
    },
    isNew() {
      return !this.dbItem;
    },
    entityBreadCrumbs() {
      var result = [];

      if (this.entityPath?.length > 0) {
        var targets = this.entities.slice();
        var ids = [];

        result.push({
          text: "All",
          path: "",
        });

        this.entityPath.split(".").forEach((id) => {
          var target = targets.find((x) => x.id === id);
          ids.push(target.id);

          result.push({
            text: target.name,
            path: ids.join("."),
          });
        });
      }

      return result;
    },
    entityOptions() {
      if (this.entitySearch === "") {
        let options = this.entities.slice();

        if (this.entityPath?.length > 0) {
          let pathParts = this.entityPath.split(this.entityPathSeparator);

          pathParts.forEach((part) => {
            options = options.find((x) => x.id === part)?.children ?? [];
          });
        }

        const transform = (entity, parentPath = this.entityPath) => {
          var path =
            parentPath?.length > 0 ? `${parentPath}.${entity.id}` : entity.id;

          const hasChildSelected = (item) => {
            var result = false;
            if (item.hasChildren) {
              item.childOptions.forEach((c) => {
                if (c.isSelected || hasChildSelected(c)) {
                  result = true;
                }
              });
            }

            return result;
          };

          var result = {
            text: entity.name,
            value: path,
            hasChildren: entity.children?.length > 0 ?? false,
            childOptions: entity.children?.map((c) => transform(c, path)),
            isSelected: this.entityOwnerClaims.some((x) => x.value === path),
          };

          result.hasChildSelected = hasChildSelected(result);
          return result;
        };

        return sortBy(
          options.map((x) => transform(x)),
          "text"
        );
      } else {
        const flatten = (arr, results = [], parent = null) => {
          for (let i = 0; i < arr.length - 1; i++) {
            var path = parent
              ? `${parent.value}${this.entityPathSeparator}${arr[i].id}`
              : arr[i].id;

            let item = {
              text: parent ? `${parent.text} > ${arr[i].name}` : arr[i].name,
              value: path,
              displayPath: parent
                ? `${parent.value} &nbsp;>&nbsp; ${arr[i].id}`
                : arr[i].id,
              hasChildren: arr[i].children?.length > 0 ?? false,
              isSelected: this.entityOwnerClaims.some((x) => x.value === path),
            };

            results.push(item);

            if (arr[i].children?.length > 0)
              flatten(arr[i].children, results, item);
          }

          return results;
        };
        var results = flatten(this.entities.slice());
        let term = this.entitySearch.toLowerCase();
        return sortBy(
          results.filter(
            (x) =>
              x.text.toLowerCase().indexOf(term) > -1 ||
              x.value.toLowerCase().indexOf(term) > -1
          ),
          "text"
        );
      }
    },
    entityPageStart() {
      return this.entityPageSize * (this.entityPage - 1);
    },
    entityPageEnd() {
      const maxIndex = this.entityOptions.length;
      let end = this.entityPageStart + this.entityPageSize;
      return end > maxIndex ? maxIndex : end;
    },
    entityPageCount() {
      return Math.ceil(this.entityOptions.length / this.entityPageSize);
    },
    entityPageDetail() {
      return `Showing ${this.entityPageStart + 1}-${this.entityPageEnd} of ${
        this.entityOptions.length
      }`;
    },
    entityPageItems() {
      return this.entityOptions.slice(this.entityPageStart, this.entityPageEnd);
    },
    entityOwnerClaims() {
      return (
        this.item.tenantClaims?.filter(
          (x) =>
            x.tenantId === this.tenantId &&
            x.key === tenantClaimTypes.entityOwner
        ) ?? []
      );
    },
    entityPathOwner() {
      return this.entityOwnerClaims.some((x) => this.entityPath == x.value);
    },
    workflows() {
      return workflows;
    },
    isAdmin() {
      return this.dbItem?.role === roles.admin;
    },
    isTenantUser() {
      return this.dbItem?.tenantClaims?.some(
        (x) => x.tenantId === this.tenantId
      );
    },
    canSubmit() {
      return (
        !this.loading &&
        !this.isAdmin &&
        !(this.isTenantUser && this.workflow == this.workflows.add)
      );
    },
  },
  watch: {
    "item.id"() {
      this.dbItemLoaded = false;
      this.loadUser();
    },
    entitySearch(val, prev) {
      if (val !== prev) {
        this.entityPage = 1;
      }
    },
    open(val) {
      if (val == false) {
        this.item = newUser();
        this.dbItem = null;
        this.$refs.observer?.reset();
      }
    },
  },
  methods: {
    edit(item) {
      this.dbItem = null;

      if (item) {
        this.item = JSON.parse(JSON.stringify(item));
        if (!this.item.tenantClaims) this.item.tenantClaims = [];
        this.loadUser();
        this.workflow = workflows.update;
      } else {
        this.item = newUser();
        this.workflow = workflows.add;
      }

      this.$refs.observer?.reset();
      this.open = true;
    },
    loadEntities() {
      this.pendingStateChanges++;
      this.loadingEntities = true;

      return entityService
        .getAll(this.tenantId)
        .then(
          (result) => {
            this.entities = result;
          },
          () => {
            this.$store.dispatch(
              "notifications/error",
              "An unexpected error occurred while loading entities. Please try again later."
            );
          }
        )
        .finally(() => {
          this.pendingStateChanges--;
          this.loadingEntities = false;
        });
    },
    toggleEntityOwnership(val) {
      let i = this.item.tenantClaims.findIndex(
        (x) =>
          x.tenantId === this.tenantId &&
          x.key === tenantClaimTypes.entityOwner &&
          x.value === val
      );

      if (i > -1) this.item.tenantClaims.splice(i, 1);
      else {
        let childMatches = this.item.tenantClaims.filter(
          (x) =>
            x.tenantId === this.tenantId &&
            x.key === tenantClaimTypes.entityOwner &&
            x.value.indexOf(val + ".") === 0
        );

        childMatches.forEach((child) => {
          let cIndex = this.item.tenantClaims.findIndex(
            (x) =>
              x.tenantId === child.tenantId &&
              x.key === child.key &&
              x.value === child.value
          );

          this.item.tenantClaims.splice(cIndex, 1);
        });

        this.item.tenantClaims.push({
          tenantId: this.tenantId,
          key: tenantClaimTypes.entityOwner,
          value: val,
        });
      }
    },
    close() {
      this.open = false;
    },
    async onSubmit() {
      const isValid = await this.$refs.observer.validate();
      if (
        !isValid ||
        ((this.isNew || this.password?.length) && !this.$refs.password.valid) ||
        !this.canSubmit
      )
        return;

      this.pendingStateChanges++;
      this.saving = true;
      let task = this.dbItem ? userService.update : userService.create;

      if (
        !this.item.tenantClaims.some(
          (x) =>
            x.tenantId === this.tenantId &&
            x.key === tenantClaimTypes.siteAccess
        )
      ) {
        this.item.tenantClaims.push({
          tenantId: this.tenantId,
          key: tenantClaimTypes.siteAccess,
          value: "true",
        });
      }

      this.item.role = roles.tenantUser;

      task(this.item)
        .then(
          () => {
            this.close();
            this.$emit("reload");
            this.$store.dispatch(
              "notifications/success",
              `Successfully ${this.isNew ? "added" : "updated"} items.`
            );
          },
          (err) => {
            if (err.statusCode === 400) {
              console.warn(err);
              this.$refs.observer.setErrors(err.result);
            } else {
              console.warn(err);
              this.$store.dispatch(
                "notifications/error",
                `An unexpected error occurred while trying to ${
                  this.isNew ? "create" : "update"
                } user. Please try again later.`
              );
            }
          }
        )
        .finally(() => {
          this.pendingStateChanges--;
          this.saving = false;
        });
    },
    loadUser: debounce(function() {
      const next = () => {
        this.pendingStateChanges++;

        userService
          .get(this.item.id)
          .then(
            (response) => {
              const data = response?.statusCode ? response.result : response;

              this.dbItem = data;
              this.dbItemLoaded = true;

              if (data) {
                this.item = JSON.parse(JSON.stringify(data));
              } else {
                var baseUser = newUser();

                for (let key in baseUser) {
                  if (key != "id") {
                    Vue.set(this.item, key, baseUser[key]);
                  }
                }
              }
            },
            (err) => {
              console.warn(err);

              this.$store.dispatch(
                "notifications/error",
                "An unexpected error occurred while loading user. Please try again later."
              );

              this.close();
            }
          )
          .finally(() => this.pendingStateChanges--);
      };

      if (this.$refs.observer) {
        if (this.$refs.observer.fields.Id.valid) next();
      } else if (this.item.id?.length > 0) next();
    }, 300),
  },
  mounted() {
    this.loadEntities();
  },
};
</script>

<style scoped>
.grey-fade-up {
  background-color: rgba(0, 0, 0, 0.75);
}
</style>
